import * as React from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { useCoarsePointer } from "@/components/landing/hooks/useCoarsePointer";

type ObliqueAppFrameProps = {
  badge?: string;
  className?: string;
  children: React.ReactNode;
  /** "hero" = more dynamic tilt; "section" = premium flat, less tilted (default) */
  variant?: "hero" | "section";
};

/** Cinematic base: moderate tilt (less "storto"), perspective 1200px */
const PERSPECTIVE_PX = 1200;
const BASE_ROTATE = { x: 10, y: -14, z: 2 } as const;
const SECTION_PARALLAX = { y: [4, -4] as const, rotZ: [1.5, 2.5] as const, tiltX: [8, 12] as const, tiltY: [-16, -12] as const };
const HERO_PARALLAX = { y: [8, -8] as const, rotZ: [0.5, 3.5] as const, tiltX: [6, 14] as const, tiltY: [-18, -10] as const };

export function ObliqueAppFrame({ badge, className, children, variant = "section" }: ObliqueAppFrameProps) {
  const reduce = useReducedMotion();
  const coarse = useCoarsePointer();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const p = variant === "hero" ? HERO_PARALLAX : SECTION_PARALLAX;

  // Parallax (section: subtler for premium flat feel)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "end 0.2"],
  });

  const yParallax = useTransform(scrollYProgress, [0, 1], [...p.y]);
  const rotZ = useTransform(scrollYProgress, [0, 1], [...p.rotZ]);

  const tiltEnabled = !reduce && !coarse;
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [...p.tiltX]), { stiffness: 140, damping: 18, mass: 0.2 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [...p.tiltY]), { stiffness: 140, damping: 18, mass: 0.2 });

  function onMove(e: React.MouseEvent) {
    if (!tiltEnabled) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    mx.set(px);
    my.set(py);
  }

  function onLeave() {
    mx.set(0);
    my.set(0);
  }

  return (
    <div ref={ref} className={cn("relative", className)} style={{ perspective: PERSPECTIVE_PX }}>
      <motion.div
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="relative will-change-transform"
        transition={{ duration: 0.16, ease: [0, 0, 0.2, 1] as [number, number, number, number] }}
        style={{
          y: reduce ? 0 : yParallax,
          rotateZ: reduce ? BASE_ROTATE.z : rotZ,
          rotateX: tiltEnabled ? rotateX : BASE_ROTATE.x,
          rotateY: tiltEnabled ? rotateY : BASE_ROTATE.y,
          transformStyle: "preserve-3d",
        }}
      >
        <motion.div
          className="relative overflow-hidden rounded-[28px] border border-black/10 bg-white dark:border-white/10 dark:bg-[#0B0D10]"
          style={{ boxShadow: "0 24px 64px -32px rgba(2,6,23,0.18)" }}
          whileHover={{
            scale: 1.01,
            boxShadow: "0 32px 80px -40px rgba(2,6,23,0.22)",
            transition: { duration: 0.16, ease: [0, 0, 0.2, 1] },
          }}
          transition={{ duration: 0.16, ease: [0, 0, 0.2, 1] as [number, number, number, number] }}
        >
          {/* highlight/vignette (decorazioni: pointer-events-none) */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/80 to-transparent dark:from-white/5 dark:to-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_10%,rgba(255,255,255,0.60)_0%,transparent_60%)] dark:bg-[radial-gradient(80%_60%_at_50%_10%,rgba(255,255,255,0.06)_0%,transparent_60%)]" />

          {/* window bar */}
          <div className="flex items-center gap-2 border-b border-black/10 px-4 py-3 dark:border-white/10">
            <div className="flex gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-black/20 dark:bg-white/20" />
              <span className="h-2.5 w-2.5 rounded-full bg-black/15 dark:bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-black/10 dark:bg-white/10" />
            </div>
            {badge ? (
              <span className="ml-3 rounded-full border border-black/10 bg-black/5 px-2.5 py-1 text-xs font-medium text-black/70 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
                {badge}
              </span>
            ) : null}
            <div className="ml-auto h-2 w-24 rounded-full bg-black/10 dark:bg-white/10" />
          </div>

          {/* CONTENT SLOT */}
          <div className="relative bg-black dark:bg-[#0B0D10]">
            {children}
            {/* gloss */}
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent_0%,rgba(255,255,255,0.10)_22%,transparent_45%)] opacity-60" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
